/*eslint complexity: ["error", 39]*/
import React from 'react';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import {
  GetPaymentDto,
  GetPaymentDtoStatusEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import momencik from '../../../common/momencik';
import { toFixed } from '../../../utils/mathUtils';
import { VisitRegistrationLink } from '../../containers/RegistrationsView/VisitRegistrationLink';
import { ExamLinkRegister } from '../Exam/ExamLinkRegister';

import { CreateSalesInvoiceFromPaymentButton } from './CreateSalesInvoiceFromPaymentButton';

interface PaymentsProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh: boolean;
  onPaymentRowClick?: (paymentId: string) => void;
  showCreateInvoiceAction?: boolean;
}

export function paymentStatusMapper(
  paymentStatus: GetPaymentDtoStatusEnum,
): string {
  switch (paymentStatus) {
    case GetPaymentDtoStatusEnum.Pending:
      return 'Oczekująca';
    case GetPaymentDtoStatusEnum.Success:
      return 'Zakończona';
    case GetPaymentDtoStatusEnum.Error:
      return 'Błąd';
    case GetPaymentDtoStatusEnum.Cancelled:
      return 'Anulowana';
    case GetPaymentDtoStatusEnum.Created:
      return 'Utworzona';
    case GetPaymentDtoStatusEnum.Returned:
      return 'Zwrócona';
  }
}
export const paymentForGrid = (
  payment: GetPaymentDto,
  setRefresh?: () => void | undefined,
) => {
  const strikethrough =
    payment.visit?.active === false ? 'text-decoration-line-through' : '';
  return {
    key: payment.id,
    values: {
      'payment.sysNumber': payment.sysNumber,
      'visit.surname':
        payment.visit &&
        `${payment.visit?.surname} ${payment.visit?.firstname}`,
      'examType.name': payment.visit?.examType?.name || '',
      'exam.number': payment.visit?.exam?.number ? (
        <ExamLinkRegister
          examId={payment.visit.exam.id}
          examNumber={payment.visit.exam.number}
        />
      ) : (
        payment.visit?.exam?.number || ''
      ),
      'visit.visitDateTime': (
        <span className={strikethrough}>
          {payment.visit?.visitDateTime
            ? momencik(payment.visit.visitDateTime, 'YYYY-MM-DD HH:mm')
            : ''}
          {payment.visit?.id && (
            <VisitRegistrationLink
              visitId={payment.visit.id}
              onRefresh={setRefresh}
              externalBooking={!!payment.visit.externalBooking}
            />
          )}
        </span>
      ),
      'appointmentChannel.name': payment.visit?.appointmentChannel?.name || '',
      'healthcareFacility.displayName': (
        <span className={strikethrough}>
          {payment.visit?.appointmentChannel?.healthcareFacility?.displayName ||
            ''}
        </span>
      ),
      'payment.amount': toFixed(payment.amount) + ' ' + payment.currency,
      'payment.status': paymentStatusMapper(payment.status),
      'payment.gatewayName': payment.gatewayName || payment.gatewayId || '',
      'payment.creationTs': momencik(payment.creationTs, 'YYYY-MM-DD HH:mm:ss'),
      'visit.sellDocumentType': payment?.visit?.sellDocumentType || '',
      'payment.isSellDocumentIssued': payment.isSellDocumentIssued ? 'Tak' : '',
      'exam.endDate': payment.visit?.exam?.endDate ? 'Zakończone' : 'W trakcie',
      'exam.sellInvoiceAmount': payment.sellInvoiceAmount
        ? toFixed(payment.sellInvoiceAmount) + ' zł'
        : '—',
      'exam.sellReceiptAmount': payment.sellReceiptAmount
        ? toFixed(payment.sellReceiptAmount) + ' zł'
        : '—',
      'invoicePayment.sysNumber': payment.invoicePayment?.systemNumber || '',
      'invoice.create': (
        <CreateSalesInvoiceFromPaymentButton payment={payment} />
      ),
    },
  };
};

export const Payments: React.FC<PaymentsProps> = props => {
  const columns = [
    { header: 'Numer', property: 'payment.sysNumber' },
    { header: 'Zarejestrowany', property: 'visit.surname' },
    { header: 'Kanał rejestracji', property: 'appointmentChannel.name' },
    { header: 'Typ badania', property: 'examType.name' },
    { header: 'Numer badania', property: 'exam.number' },
    { header: 'Data i czas wizyty', property: 'visit.visitDateTime' },
    { header: 'Placówka', property: 'healthcareFacility.displayName' },
    { header: 'Kwota', property: 'payment.amount' },
    { header: 'Sposób płatności', property: 'payment.gatewayName' },
    { header: 'Status', property: 'payment.status', noFilter: true },
    { header: 'Data utworzenia', property: 'payment.creationTs' },
    { header: 'Potwierdzenie', property: 'visit.sellDocumentType' },
    {
      header: 'Potw. płatności',
      property: 'payment.isSellDocumentIssued',
      noFilter: true,
    },
    {
      header: 'Status badania',
      property: 'exam.endDate',
      noFilter: true,
      noSorting: true,
    },
    {
      header: 'Faktura',
      property: 'exam.sellInvoiceAmount',
      noFilter: true,
      noSorting: true,
    },
    {
      header: 'Paragon',
      property: 'exam.sellReceiptAmount',
      noFilter: true,
      noSorting: true,
    },
    {
      header: 'Nr sys. wpłaty',
      property: 'invoicePayment.sysNumber',
    },
    ...(props.showCreateInvoiceAction
      ? [
          {
            header: '',
            property: 'invoice.create',
            noSorting: true,
            noFilter: true,
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="payment.sysNumber"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage="Brak płatności w systemie"
      enableMultiSelect={false}
      refresh={props.refresh}
      withFilterInColumns={true}
      onRowClick={id => {
        if (props.onPaymentRowClick && id) {
          props.onPaymentRowClick(id);
        }
      }}
    />
  );
};
